import type { ModuleSnapshot } from '@ctcells/contract'
import { useCanCall, useMe, useRecordConsumableReplenishment } from '@ctcells/api'
import { t } from '@ctcells/i18n'

import { gate, type ActionGate } from '../access.ts'
import { useScreenAccess } from '../screenAccess.ts'

import { findModule } from './monitoringModel.ts'
import type { ReplenishView } from './SupplyReplenishDialog.tsx'
import type { SupplyKey, SupplySummary } from './supplySummary.ts'

/**
 * **모니터링의 「보충」 값을 짓는 손**(eric 2026-10-02 — 상세 팝오버를 걷으면서 보충 판정이 팝오버 컨테이너
 * `SupplyDetailContainer` 에서 모니터링 컨테이너로 옮겨 왔다. 판정 자체(`replenishViewOf`)는 한 줄도 안 바뀌었다).
 *
 * 🔴 **훅은 조기 반환보다 위에서 부른다** — 그래서 값(요약 · 모듈)은 나중에 받는 함수를 돌려준다.
 * 🔴 판정(종전 그대로): 제어락 비대상 · 역할(operator · admin)만 본다(`modules.py` `WRITE_ROLES`). `/auth/me` 의 operations 를
 *    알면 그것, 모르면 권한 표(`control`). 스트림이 끊겨 stale 이면 막는다. 목 모드(`live` 거짓)면 막는다.
 * 🔴 숨기지 않는다 — 권한이 없어도 버튼은 서고 비활성 + 사유다.
 */
export function useSupplyReplenish(
  live: boolean,
): (
  summary: SupplySummary,
  modules: readonly ModuleSnapshot[],
) => (supplyKey: SupplyKey) => ReplenishView | undefined {
  const access = useScreenAccess()
  const { operations } = useMe()
  const canReplenish = useCanCall('recordModuleConsumableReplenishment')
  const command = useRecordConsumableReplenishment()
  return (summary, modules) => (supplyKey) =>
    replenishViewOf({
      supplyKey,
      summary,
      moduleId: findModule(modules, supplyKey === 'tcstock' ? 'asm' : 'stain')?.module_id,
      live,
      disabledByGate: gate(access, { perm: 'control' }),
      streamStale: access.controlDisabled === true,
      operationsKnown: operations !== null,
      canReplenish,
      send: (vars) => command.mutateAsync(vars),
    })
}

/**
 * 보충 창에 넘길 값 — 팁 · TC 만(시약은 `undefined` = 버튼 없음).
 * 🔴 만재 수는 요약이 읽은 칸 수(랩웨어 정의)다. 팁은 두 랙 합계 하나(서버 보충이 모듈 합계라서).
 */
export function replenishViewOf({
  supplyKey,
  summary,
  moduleId,
  live,
  disabledByGate,
  streamStale,
  operationsKnown,
  canReplenish,
  send,
}: {
  supplyKey: SupplyKey
  summary: SupplySummary
  moduleId: string | undefined
  live: boolean
  disabledByGate: ActionGate
  streamStale: boolean
  operationsKnown: boolean
  canReplenish: boolean
  send: (vars: {
    moduleId: string
    kind: 'tip' | 'tc'
    count: number
    note?: string
  }) => Promise<unknown>
}): ReplenishView | undefined {
  if (supplyKey === 'reagents') return undefined
  const kind = supplyKey === 'tcstock' ? 'tc' : 'tip'
  const items = summary.items.filter((item) =>
    kind === 'tc' ? item.key === 'tcstock' : item.key === 'tips-1' || item.key === 'tips-2',
  )
  const full = items.reduce((sum, item) => sum + item.total, 0)
  const current =
    kind === 'tip'
      ? (summary.tipsReported?.remaining ??
        (items.every((item) => item.remaining !== null)
          ? items.reduce((sum, item) => sum + (item.remaining ?? 0), 0)
          : undefined))
      : (items[0]?.remaining ?? undefined)
  /* 사유 — 차례: 목 모드 → 모듈 없음 → 연결 끊김 → 권한(operations 를 알면 그것, 모르면 권한 표) */
  const disabledReason = !live
    ? t('monitoring.supply.replenish.demo')
    : moduleId === undefined
      ? t('monitoring.supply.replenish.noModule')
      : streamStale
        ? t('reason.streamStale')
        : operationsKnown
          ? canReplenish
            ? undefined
            : t('reason.noPermission', { perm: 'control' })
          : disabledByGate.disabled
            ? disabledByGate.disabledReason
            : undefined
  return {
    kind,
    moduleName: t(`module.${kind === 'tc' ? 'asm' : 'stain'}`),
    full,
    ...(current === undefined ? {} : { current }),
    ...(disabledReason === undefined ? {} : { disabledReason }),
    onSubmit: ({ count, note }) =>
      moduleId === undefined
        ? Promise.reject(new Error(t('monitoring.supply.replenish.noModule')))
        : send({ moduleId, kind, count, ...(note === undefined ? {} : { note }) }),
  }
}
